/*
	bf_turnvictory.ts
    Copyright (c) 2013-present Human Cube Inc.

    Checks and assigns victory if obtained.

    Must all be synchronous and not involve any external DB or file access.  Also does not access
    any external user data.  These restrictions are to allow it to be re-used in other environments
    such as the client.
*/

import BF from '../bfconst1';
import bfH from '../bf_helper';
import { Game } from '../gameTypes';
import bfTurnReplay from './bf_turnreplay';


export function checkForVictory (game: Game) {
  // Checks for and assigns victory status in the game object.
  if( game.teams <= 1 ) {
    checkForSoloVictory(game);
  }
  else {
    checkForTeamVictory(game);
  }
};


function assignFinishForAllSoloPlayers (game: Game, playerValue: number[]) {
    // Game is ending.  Assign a finishing rank for all remaining players in a solo game.
    let lowestValue;
    do {
        // find the lowest ranks first (assign ties the same lowest rank.)
        let rank = 0;
        for(let i = 1; i <= game.players; i++)
            if( !game.finished[i][0] )  // Player still in the game.
                rank++;
        if( rank === 0 )
            break;  // No more players to assign.

        lowestValue = 0;
        for(let i = 1; i <= game.players; i++)
            if( !game.finished[i][0] )  // Player still in the game.
                if( playerValue[i] > 0 && (lowestValue === 0 || lowestValue > playerValue[i]) )
                    lowestValue = playerValue[i];
        if( lowestValue > 0 ) {
            for(let i = 1; i <= game.players; i++)
                if( playerValue[i] === lowestValue )
                    bfH.setPlayerFinish(game, i, rank);
        }
    } while( lowestValue > 0 );
}


function checkForSoloVictory (game: Game) {

    // First check if there is a winner simply by elimination:

    let remainingCount = 0;  // Players remaining count.
    let lastPlayerIndex = 0;

    for(let playerIndex = 1; playerIndex <= game.players; playerIndex++)
        if( !game.finished[playerIndex][0] ) {  // Player still in the game.
            remainingCount++;
            lastPlayerIndex = playerIndex;
        }
    if( remainingCount === 1 ) {
        // Single winner.
        bfH.setPlayerFinish(game, lastPlayerIndex, 1);
        bfTurnReplay.victory(game, [lastPlayerIndex], false, BF.VICTORY_ELIMINATION);
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }
    if( remainingCount === 0 ) {
        // Tie for victor.  So find the best ranked as winners.
        let best = 1000000;
        let bestPlayer: number[] = [];
        for(let playerIndex = 1; playerIndex <= game.players; playerIndex++) {
            if( game.finished[playerIndex][0] < best ) {
                best = game.finished[playerIndex][0];
                bestPlayer = [playerIndex];
            }
            else if( game.finished[playerIndex][0] === best ) {
                bestPlayer.push(playerIndex);
            }
        }
        bfTurnReplay.victory(game, bestPlayer, false, BF.VICTORY_ELIMINATION);
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }


    // Next check if there is a winner by victory condition(s):

    let playerWorth = [];
    let playerVictoryFlags = [];

    let best = 0;
    let bestIDs: number[] = [];

    for(let playerIndex = 1; playerIndex <= game.players; playerIndex++) {
        playerWorth[playerIndex] = 0;
        playerVictoryFlags[playerIndex] = 0;

        if( !game.finished[playerIndex][0] ) {  // Player still in the game.
            let zonesOwned = 0;
            let income = 0;
            for(let j = 1; j <= game.zones; j++) {
                if( game.zoneOwner[j] === playerIndex ) {
                    zonesOwned++;
                    income += game.zoneIncome[j];
                }
            }
            playerWorth[playerIndex] += income * 16;

            let forcesValue = 0;
            for(let j = 0; j < game.force[playerIndex].length; j++) {
                let force = game.force[playerIndex][j];
                if( force[BF.FORCE_INDEX_ZONE] >= 0 ||
                        force[BF.FORCE_INDEX_ZONE] <= BF.FORCE_ZONE_CARGO ) {
                    forcesValue += game.forceCost[force[BF.FORCE_INDEX_TYPE]];
                }
            }
            playerWorth[playerIndex] += forcesValue;

            if( game.victoryLandZones > 0 && game.victoryLandZones <= zonesOwned) {
                playerWorth[playerIndex] += 1000000;
                playerVictoryFlags[playerIndex] |= BF.VICTORY_ZONES;
            }
            if( game.victoryIncome > 0 && game.victoryIncome <= income) {
                playerWorth[playerIndex] += 1000000;
                playerVictoryFlags[playerIndex] |= BF.VICTORY_INCOME;
            }
            if( game.victoryCash > 0 && game.victoryCash <= game.cash[playerIndex] ) {
                playerWorth[playerIndex] += 1000000;
                playerVictoryFlags[playerIndex] |= BF.VICTORY_CASH;
            }
            if( game.victoryCapitals > 0 ) {
                let capitals = 0;
                for(let j = 1; j <= game.players; j++)
                    if( game.capital[j] > 0 && game.zoneOwner[game.capital[j]] === playerIndex )
                        capitals++;
                if( game.victoryCapitals <= capitals ) {
                    playerWorth[playerIndex] += 1000000;
                    playerVictoryFlags[playerIndex] |= BF.VICTORY_CAPITALS;
                }
            }

            if( playerWorth[playerIndex] > best ) {
                best = playerWorth[playerIndex];
                bestIDs = [playerIndex];
            }
            else if( playerWorth[playerIndex] === best ) {
                bestIDs.push(playerIndex);
            }
        }
    }

    if( best >= 1000000 ) {   // At least one victory condition was obtained.
        assignFinishForAllSoloPlayers(game, playerWorth);
        bfTurnReplay.victory(game, bestIDs, false, playerVictoryFlags[bestIDs[0]]);
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }

    // Finally check if players have lost interest in the game:
    if( game.autoCurrent > game.autoAllowed ) {
        // End game, as all humans have lost interest, they have all autoplaying too long.
        assignFinishForAllSoloPlayers(game, playerWorth);
        bfTurnReplay.victory(game, [], false, BF.VICTORY_AUTOPLAY_LIMIT);
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }

    // Finally finally check if there are any human players left playing:
    let humansStillPlaying = 0;
    for(let i = 1; i <= game.players; i++)  // All players.
        if( (game.playerFlags[i] & BF.PLAYER_FLAG_HUMAN) && !game.finished[i][0] )
            humansStillPlaying++;
    if( humansStillPlaying === 0 ) {
        // End game, as all humans have been eliminated.
        assignFinishForAllSoloPlayers(game, playerWorth);
        bfTurnReplay.victory(game, [], false, BF.VICTORY_NO_HUMANS_LEFT);
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }

    // Finally finally finally check if turn limit is up:
    if( game.maxTurns && game.maxTurns > 0 &&
            game.turn >= BF.TURN_NORMAL + game.maxTurns ) {
        // Maximum turns reached.
        assignFinishForAllSoloPlayers(game, playerWorth);
        bfTurnReplay.victory(game, [], false, BF.VICTORY_TURN_LIMIT);
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }

    // Finally finally finally finally check if game time limit is up:
    if(game.started && game.maxDuration && game.maxDuration > 0) {
        const dateStarted = new Date(game.started);
        const nowMS = Date.now();
        if( nowMS - dateStarted.getTime() > game.maxDuration * 1000 ) {
            // Maximum full game time reached.
            assignFinishForAllSoloPlayers(game, playerWorth);
            bfTurnReplay.victory(game, [], false, BF.VICTORY_TIME_LIMIT);
            if( game.state === BF.GAME_STATE_STARTED )
                game.state = BF.GAME_STATE_FINISHED;
            return;
        }
    }
}


function setOrUpdateAllPlayerTeamFinishes (game: Game, winningTeam: number) {
  // winningTeam can be set to -1 to signify no winning team.
  // Sets all players on winning team to rank 1 and all others to rank 2.
  // Returns an array of playerIndexes on the winning team.
  const winners: number[] = [];
  for(let playerIndex = 1; playerIndex <= game.players; playerIndex++) {
    if( game.team[playerIndex] === winningTeam ) {
      // Set all members of that team as winners:
      bfH.setOrUpdatePlayerFinish(game, playerIndex, 1);
      winners.push(playerIndex);
    }
    else bfH.setOrUpdatePlayerFinish(game, playerIndex, 2);
  }
  return winners;
}


function checkForTeamVictory (game: Game) {

    // First check if there is a winner simply by elimination:

    let remainingTeamCount = 0;
    let lastTeamIndex = 0;

    // Teams remaining count, players not in a team are each counted as a remaining additional team.
    for(let playerIndex = 1; playerIndex <= game.players; playerIndex++)
        if( !game.finished[playerIndex][0] ) {  // Player still in the game.
            let teamIndex = game.team[playerIndex];
            if( teamIndex < 1 || lastTeamIndex !== teamIndex ) {
                remainingTeamCount++;
                if( teamIndex > 0 )
                    lastTeamIndex = teamIndex;
            }
        }
    if( remainingTeamCount === 1 ) {
        // Single team winner.
        let winners = setOrUpdateAllPlayerTeamFinishes(game, lastTeamIndex);
        bfTurnReplay.victory(game, winners, lastTeamIndex, BF.VICTORY_ELIMINATION);
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }
    if( remainingTeamCount === 0 ) {
        // All players have been eliminated.  So find the best ranked team as winners.  If more
        // than one winning team, then set all to 2nd place.
        let best = 1000000;
        let bestTeamIndex = -1;
        for(let playerIndex = 1; playerIndex <= game.players; playerIndex++) {
            let team = game.team[playerIndex];
            if( game.finished[playerIndex][0] < best ) {
                best = game.finished[playerIndex][0];
                bestTeamIndex = team;
            }
            else if( game.finished[playerIndex][0] === best ) {
                if( bestTeamIndex !== team )
                    bestTeamIndex = -1;  // Currently two or more teams tied at this rank.
            }
        }
        const winners = setOrUpdateAllPlayerTeamFinishes(game, bestTeamIndex);
        bfTurnReplay.victory(
            game,
            winners,
            bestTeamIndex > 0 ? bestTeamIndex : false,
            BF.VICTORY_ELIMINATION,
        );
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }


    // Next check if there is a winning team by victory condition(s):

    let teamWorth = [];
    let teamVictoryFlags = [];

    let teamZonesOwned = [];
    let teamIncome = [];
    let teamForceCount = [];
    let teamCash = [];

    for(let teamIndex = 0; teamIndex <= game.teams; teamIndex++) {
        teamWorth[teamIndex] = 0;
        teamVictoryFlags[teamIndex] = 0;

        teamZonesOwned[teamIndex] = 0;
        teamIncome[teamIndex] = 0;
        teamForceCount[teamIndex] = 0;
        teamCash[teamIndex] = 0;
    }

    for(let playerIndex = 1; playerIndex <= game.players; playerIndex++) {
        if( !game.finished[playerIndex][0] ) {  // Player still in the game.
            let teamIndex = game.team[playerIndex];

            for(let j = 1; j <= game.zones; j++) {
                if( game.zoneOwner[j] === playerIndex ) {
                    teamZonesOwned[teamIndex]++;
                    teamIncome[teamIndex] += game.zoneIncome[j] * 16;
                }
            }

            for(let j = 0; j < game.force[playerIndex].length; j++) {
                let force = game.force[playerIndex][j];
                if( force[BF.FORCE_INDEX_ZONE] >= 0 ||
                        force[BF.FORCE_INDEX_ZONE] <= BF.FORCE_ZONE_CARGO)
                    teamForceCount[teamIndex]++;
                    teamWorth[teamIndex] += game.forceCost[force[BF.FORCE_INDEX_TYPE]];
            }

            teamWorth[teamIndex] += game.cash[playerIndex];
            teamCash[teamIndex] += game.cash[playerIndex];
        }
    }

    let best = 0;
    let bestIDs: number[] = [];

    for(let teamIndex = 0; teamIndex <= game.teams; teamIndex++) {
        if( game.victoryLandZones > 0 &&
                game.victoryLandZones <= teamZonesOwned[teamIndex]) {
            teamWorth[teamIndex] += 1000000;
            teamVictoryFlags[teamIndex] |= BF.VICTORY_ZONES;
        }
        if( game.victoryIncome > 0 && game.victoryIncome <= teamIncome[teamIndex]) {
            teamWorth[teamIndex] += 1000000;
            teamVictoryFlags[teamIndex] |= BF.VICTORY_INCOME;
        }
        if( game.victoryCash > 0 && game.victoryCash <= teamCash[teamIndex] ) {
            teamWorth[teamIndex] += 1000000;
            teamVictoryFlags[teamIndex] |= BF.VICTORY_CASH;
        }
        if( game.victoryCapitals > 0 ) {
            let capitals = 0;
            for(let p = 1; p <= game.players; p++) {
              if(game.team[p] === teamIndex) {
                if( game.capital[p] > 0 && game.zoneOwner[game.capital[p]] === p ) {
                  capitals++;
                }
              }
            }
            if( game.victoryCapitals <= capitals ) {
              teamWorth[teamIndex] += 1000000;
              teamVictoryFlags[teamIndex] |= BF.VICTORY_CAPITALS;
            }
        }

        if( teamWorth[teamIndex] > best ) {
            best = teamWorth[teamIndex];
            bestIDs = [teamIndex];
        }
        else if( teamWorth[teamIndex] === best ) {
            bestIDs.push(teamIndex);
        }
    }

    if( best >= 1000000 ) {   // At least one victory condition was obtained.
        if( bestIDs.length === 1 ) {
            // A single team was the winner.
            let winners = setOrUpdateAllPlayerTeamFinishes(game, bestIDs[0]);
            bfTurnReplay.victory(game, winners, bestIDs[0], teamVictoryFlags[bestIDs[0]]);
        }
        else{
            // More than one team tied, so set all to rank 2.
            setOrUpdateAllPlayerTeamFinishes(game, -1);
            bfTurnReplay.victory(game, [], false, BF.VICTORY_ELIMINATION);
        }
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }

    // Finally check if there are any human players left playing:
    let humansStillPlaying = 0;
    for(let i = 1; i <= game.players; i++)  // All players.
        if( (game.playerFlags[i] & BF.PLAYER_FLAG_HUMAN) && !game.finished[i][0] )
            humansStillPlaying++;
    if( humansStillPlaying === 0 ) {
        // End game, as all humans have been eliminated.
        setOrUpdateAllPlayerTeamFinishes(game, 0);
        bfTurnReplay.victory(game, [], false, BF.VICTORY_NO_HUMANS_LEFT);
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }

    // Finally finally check if turn limit is up:
    if( game.maxTurns && game.maxTurns > 0 &&
            game.turn >= BF.TURN_NORMAL + game.maxTurns ) {
        // Maximum turns reached.
        setOrUpdateAllPlayerTeamFinishes(game, 0);
        bfTurnReplay.victory(game, [], false, BF.VICTORY_TURN_LIMIT);
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }

    // Finally finally finally check if game time limit is up:
    if(game.started && game.maxDuration && game.maxDuration > 0) {
        const dateStarted = new Date(game.started);
        const nowMS = Date.now();
        if( nowMS - dateStarted.getTime() > game.maxDuration * 1000 ) {
            // Maximum full game time reached.
            setOrUpdateAllPlayerTeamFinishes(game, 0);
            bfTurnReplay.victory(game, [], false, BF.VICTORY_TIME_LIMIT);
            if( game.state === BF.GAME_STATE_STARTED )
                game.state = BF.GAME_STATE_FINISHED;
            return;
        }
    }

    // Last Finally check if players have lost interest in the game:
    if( game.autoCurrent > game.autoAllowed ) {
        // End game, as all humans have lost interest, they have all autoplaying too long.
        setOrUpdateAllPlayerTeamFinishes(game, 0);
        bfTurnReplay.victory(game, [], false, BF.VICTORY_AUTOPLAY_LIMIT);
        if( game.state === BF.GAME_STATE_STARTED )
            game.state = BF.GAME_STATE_FINISHED;
        return;
    }
}
